import Box from '@mui/material/Box';
import { MouseEvent, ReactNode, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { find, map } from 'lodash-es';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { useAuth } from 'src/auth/useAuth';
import DashboardListPageLayout from 'src/common/components/dashboard/DashboardListPageLayout';
import ListFilterEmptyState from 'src/common/components/dashboard/ListFilterEmptyState';
import Empty from 'src/common/components/Empty';
import { ticketCardGridItemSx, ticketCardGridSize, ticketCardScrollContainerSx } from 'src/common/styles/ticketCardStyles';
import TechnicalTypeCard from 'src/common/components/TechnicalTypeCard';
import { dashboardPrimaryActionButtonProps } from 'src/common/components/DashboardPageToolbar';
import TechnicalTypeListFilterForm from 'src/common/components/list-filters/TechnicalTypeListFilterForm';
import { LanguageKeys } from 'src/common/constants/languages';
import { filterTechnicalTypes } from 'src/helpers/filterTechnicalTypes';
import { FormTechnicalType } from 'src/common/forms/TechnicalTypeEditorForm';
import usePersistentView from 'src/hooks/usePersistentView';
import { useTechnicalTypeListFilters } from 'src/hooks/useTechnicalTypeListFilters';
import TechnicalTypeEditorModal from 'src/common/modals/TechnicalTypeEditorModal';
import TechnicalTypesTable from 'src/common/tables/TechnicalTypesTable';
import { TechnicalType } from 'src/interfaces/TechnicalType';
import { EView } from 'src/interfaces/View';

type TechnicalTypeEntity = 'distances' | 'accommodations' | 'competitions';

interface TechnicalTypesStoreSelectors {
  items: TechnicalType[];
  isLoading: boolean;
  fetchItems: () => Promise<unknown>;
  saveItem: (item: FormTechnicalType, id?: string) => Promise<unknown>;
  deleteItem: (id: string) => Promise<unknown>;
  deleteAllItems: () => Promise<unknown>;
}

interface TechnicalTypesI18nKeys {
  prefix: `system.${TechnicalTypeEntity}`;
  emptyPrefix: `empty.${TechnicalTypeEntity}`;
  errorsPrefix: `system.${TechnicalTypeEntity}.errors`;
}

interface TechnicalTypesManagerPageProps {
  entityType: TechnicalTypeEntity;
  storeSelectors: TechnicalTypesStoreSelectors;
  i18nKeys: TechnicalTypesI18nKeys;
  emptyIcon: ReactNode;
  editorModalId: string;
  defaultIcon?: string;
  showDescription?: boolean;
  withIcon?: boolean;
}

const TechnicalTypesManagerPage = ({
  storeSelectors,
  i18nKeys,
  emptyIcon,
  editorModalId,
  defaultIcon,
  showDescription = true,
  withIcon = false,
}: TechnicalTypesManagerPageProps) => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();

  const { items, isLoading, fetchItems, saveItem, deleteItem, deleteAllItems } = storeSelectors;
  const { isSuperAdmin } = useAuth();

  const [view, setView] = usePersistentView();
  const { query, publishedFilter, order, orderByKey, setQuery, setPublishedFilter, setOrder, setOrderByKey } =
    useTechnicalTypeListFilters();
  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<TechnicalType | undefined>(undefined);

  const filteredItems = useMemo(
    () =>
      filterTechnicalTypes(items, currentLang, {
        query,
        publishedFilter,
        sortBy: orderByKey,
        sortOrder: order,
      }),
    [items, currentLang, query, publishedFilter, orderByKey, order],
  );

  const handleOpenEditorModal = (id?: string, e?: MouseEvent<unknown>) => {
    e && e.stopPropagation();
    setSelectedItem(find(items, { id }));
    setEditorModalOpen(true);
  };

  const handleCloseEditorModal = () => {
    setSelectedItem(undefined);
    setEditorModalOpen(false);
  };

  const handleDeleteAll = async (e?: MouseEvent<unknown>) => {
    e && e.stopPropagation();
    const { confirmed } = await confirm({
      title: t(`${i18nKeys.prefix}.deleteAllConfirm`),
      description: t(`${i18nKeys.prefix}.deleteDescription`),
      confirmationText: t('confirmProvider.confirmationText'),
      cancellationText: t('confirmProvider.cancellationText'),
    });
    if (!confirmed) {
      enqueueSnackbar(t(`${i18nKeys.prefix}.deleteErrorMessage`), {
        variant: 'error',
      });
      return;
    }
    await deleteAllItems()
      .then(() => {
        enqueueSnackbar(t(`${i18nKeys.prefix}.deleteSuccessfulMessage`), {
          variant: 'success',
        });
      })
      .catch(
        ({
          response: {
            data: { errorCode, message },
          },
        }) => {
          enqueueSnackbar(errorCode ? t(`${i18nKeys.errorsPrefix}.${errorCode}`) : message, {
            variant: 'error',
          });
        },
      );
  };

  const handleDelete = async (id: string, e?: MouseEvent<unknown>) => {
    e && e.stopPropagation();
    const { confirmed } = await confirm({
      title: t(`${i18nKeys.prefix}.deleteConfirm`),
      description: t(`${i18nKeys.prefix}.deleteDescription`),
      confirmationText: t('confirmProvider.confirmationText'),
      cancellationText: t('confirmProvider.cancellationText'),
    });
    if (!confirmed) {
      enqueueSnackbar(t(`${i18nKeys.prefix}.deleteErrorMessage`), {
        variant: 'error',
      });
      return;
    }
    await deleteItem(id);

    enqueueSnackbar(t(`${i18nKeys.prefix}.deleteSuccessfulMessage`), {
      variant: 'success',
    });
  };

  const handleSave = async (item: FormTechnicalType, id?: string) => {
    await saveItem(item, id);
    await fetchItems();
    const saveSuccessKey =
      i18nKeys.prefix === 'system.distances'
        ? 'saveDistanceSuccess'
        : i18nKeys.prefix === 'system.accommodations'
          ? 'saveAccommodationSuccess'
          : 'saveCompetitionSuccess';
    enqueueSnackbar(t(`${i18nKeys.prefix}.${saveSuccessKey}`), {
      variant: 'success',
    });
    handleCloseEditorModal();
  };

  const deleteAllLabelKey =
    i18nKeys.prefix === 'system.distances'
      ? 'deleteAllDistances'
      : i18nKeys.prefix === 'system.accommodations'
        ? 'deleteAllAccommodations'
        : 'deleteAllCompetitions';

  return (
    <>
      <DashboardListPageLayout
        title={t(`${i18nKeys.prefix}.pageTitle`)}
        isLoading={isLoading}
        headerActions={
          <>
            {isSuperAdmin && (
              <Tooltip title={t(`${i18nKeys.prefix}.${deleteAllLabelKey}`)}>
                <IconButton
                  onClick={handleDeleteAll}
                  color="error"
                  aria-label={t(`${i18nKeys.prefix}.${deleteAllLabelKey}`)}
                >
                  <DeleteForeverIcon />
                </IconButton>
              </Tooltip>
            )}
            <Button
              {...dashboardPrimaryActionButtonProps()}
              onClick={() => handleOpenEditorModal()}
              startIcon={<AddIcon />}
            >
              {t(`${i18nKeys.prefix}.new`)}
            </Button>
          </>
        }
        toolbar={
          <TechnicalTypeListFilterForm
            query={query}
            setQuery={setQuery}
            publishedFilter={publishedFilter}
            setPublishedFilter={setPublishedFilter}
            order={order}
            orderByKey={orderByKey}
            setOrder={setOrder}
            setOrderByKey={setOrderByKey}
            view={view}
            onSetView={setView}
          />
        }
      >
        {!isLoading && items.length === 0 && (
          <Empty
            icon={emptyIcon}
            title={t(`${i18nKeys.emptyPrefix}.title`)}
            description={t(`${i18nKeys.emptyPrefix}.description`)}
            primaryAction={{
              label: t('empty.addNew'),
              onClick: () => handleOpenEditorModal(),
            }}
          />
        )}
        {!isLoading && items.length > 0 && filteredItems.length === 0 && <ListFilterEmptyState />}
        {filteredItems.length > 0 && (
          <Box sx={ticketCardScrollContainerSx(view !== EView.table)}>
            {view === EView.grid && (
              <Grid container spacing={2} sx={{ overflow: 'visible' }}>
                {map(filteredItems, item => (
                  <Grid size={ticketCardGridSize} key={item.id} sx={ticketCardGridItemSx}>
                    <TechnicalTypeCard
                      item={item}
                      defaultIcon={defaultIcon}
                      titleBesideAvatar
                      showDescription={showDescription}
                      onDelete={handleDelete}
                      onEdit={handleOpenEditorModal}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
            {view === EView.table && (
              <TechnicalTypesTable
                items={filteredItems}
                onDelete={handleDelete}
                onEdit={handleOpenEditorModal}
              />
            )}
          </Box>
        )}
      </DashboardListPageLayout>
      {editorModalOpen && (
        <TechnicalTypeEditorModal
          id={editorModalId}
          open={editorModalOpen}
          handleClose={handleCloseEditorModal}
          handleSave={handleSave}
          item={selectedItem}
          withIcon={withIcon}
        />
      )}
    </>
  );
};

export default TechnicalTypesManagerPage;
